import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { ExportMenu, type ExportMenuHandle } from '../../components/AppToolbar/ExportMenu';
import { LoadTimeChip } from '../../components/LoadTimeChip';
import { LoaderSymbol } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useConfirm } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useAuthStore } from '../../auth/store';
import { useNavPermission } from '../../hooks/useNavPermission';
import { fmtLocalDate } from '../../utils/datetime';
import { ComboSelect } from '../../components/ComboSelect';
import './sampleIn.css';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface ToOptions { storages: string[]; users: string[]; }

/** Amber "required to proceed" highlight for the scan input + destination select
 *  (matches the Sample-Tracking prototype's orange-tinted Barcode Scan / To fields
 *  and the app's required-field tint, §18.6 rule 12). */
const REQ_FIELD: CSSProperties = {
  // 28px fixed height (user request 2026-09-19) — box-sizing:border-box so the
  // 1px border + padding sit inside the 28px.
  border: '1px solid #ff8c42', borderRadius: 4, padding: '0 10px',
  height: 'var(--ai-control-h, 28px)', boxSizing: 'border-box',
  fontSize: 13, background: '#fffbe7', color: 'var(--ai-ink)',
};

interface ResolveResult {
  ok:             boolean;
  error:          string | null;
  sampleBottleId: string | null;
  sampleId:       string | null;
  jobId:          string | null;
  client:         string | null;
  sampleLoginOid: string | null;
  jobOid:         string | null;
}

/** One scanned-but-unsaved custody line held in the working grid. */
interface ScanRow {
  id:             string;   // client-only key
  sampleBottleId: string;
  jobId:          string;   // sent to the API; not shown (prototype's 7-col grid)
  client:         string;   // sent to the API; not shown
  from:           string;
  to:             string;
  scannedAt:      string;   // ISO — display only; server stamps the real DateHandled
  handledBy:      string;
  hazardous:      boolean;
  mode:           string;
  sampleLoginOid: string | null;
  jobOid:         string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component — ALPACA-parity Barcode Sample Custody → Sample In entry form,
// laid out to match the Sample-Tracking module prototype: top + bottom Save/Cancel
// bars, a Barcode Scan + To band, and the "Barcode Transaction" grid.
// (CLAUDE.md §18.11b — legacy SampleInOutController, SampleCustodyTest_DetailView_SampleIn.)
// ─────────────────────────────────────────────────────────────────────────────
export default function SampleInPage() {
  const [opts,     setOpts]     = useState<ToOptions>({ storages: [], users: [] });
  const [to,       setTo]       = useState('');
  const [barcode,  setBarcode]  = useState('');
  const [rows,     setRows]     = useState<ScanRow[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [scanning, setScanning] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [loadMs,   setLoadMs]   = useState<number | null>(null);
  const [loading,  setLoading]  = useState(true);

  const barcodeRef = useRef<HTMLInputElement>(null);
  // Ref to the grid-header "Export to" menu so Alt+X opens it (the labelled Export
  // button lives in the grid's headerLeft; the grid's built-in icon is hidden).
  const exportRef = useRef<ExportMenuHandle>(null);
  const notify   = useNotify();
  const runSave  = useSaveFlow();
  const confirm  = useConfirm();
  const userName = useAuthStore(s => s.user?.name ?? '');
  // MAC write gate (ALPACA parity): Save enabled only when the role is granted
  // Write on the "Sample In" NavigationItem. Server enforces the same on POST.
  const perm = useNavPermission('Sample In');
  const canWrite = perm.canWrite || perm.isAdmin;

  // ── Load the "To" destination options once ──────────────────────────────────
  useEffect(() => {
    let alive = true;
    const t0 = performance.now();
    (async () => {
      try {
        const res = await api.get('/api/v1/sample-custody/to-options');
        if (alive) setOpts((res.data as { data: ToOptions }).data ?? { storages: [], users: [] });
      } catch {
        if (alive) notify.error('Failed to load destinations.');
      } finally {
        if (alive) { setLoadMs(performance.now() - t0); setLoading(false); }
      }
    })();
    return () => { alive = false; };
  }, [notify]);

  // Keep the scan box focused for continuous scanning (DWO report 2026-10-24).
  // The input is disabled={scanning} during each resolve, so the old inline
  // barcodeRef.focus() in scanCode's finally fired while it was still disabled —
  // a no-op — and the cursor was lost after every scan, forcing a manual click.
  // Refocusing from an effect runs AFTER the input re-enables, so focus sticks;
  // it also covers the initial mount and the post-Save grid reset.
  useEffect(() => {
    if (!scanning && !saving && canWrite) barcodeRef.current?.focus();
  }, [scanning, saving, canWrite]);

  // ── Scan one barcode: validate, resolve, add a working row (ALPACA
  //    ObjectSpace_ObjectChanged "In" branch). Auto-fires when the barcode field
  //    commits (blur / Enter / scanner terminator) OR the To changes — NOT only
  //    on Enter — matching ALPACA's ImmediatePostData editors. `code`/`toVal` are
  //    passed explicitly so the To-change handler can use the just-picked value. ─
  const scanBusy = useRef(false);
  const scanCode = useCallback(async (rawCode: string, toVal: string) => {
    const code = (rawCode ?? '').trim();
    if (!code || scanBusy.current) return;
    // ALPACA: "To" is required before a scan is accepted.
    if (!toVal) { notify.error("Column 'To' should not be empty."); return; }
    // Already in the current (unsaved) batch.
    if (rows.some(r => r.sampleBottleId.toUpperCase() === code.toUpperCase())) {
      notify.error("This 'Bottle ID' exists already. Please enter a new 'Bottle ID'.");
      setBarcode('');
      return;
    }
    scanBusy.current = true;
    setScanning(true);
    try {
      const res = await api.post('/api/v1/sample-custody/resolve', { bottleId: code });
      const r = (res.data as { data: ResolveResult }).data;
      if (!r.ok) { notify.error(r.error ?? "Please enter a valid 'Bottle ID'."); setBarcode(''); return; }
      setRows(prev => [
        {
          id:             `${code}-${prev.length}-${performance.now()}`,
          sampleBottleId: r.sampleBottleId ?? code.toUpperCase(),
          jobId:          r.jobId ?? '',
          client:         r.client ?? '',
          from:           userName,
          to:             toVal,
          scannedAt:      new Date().toISOString(),
          handledBy:      userName,
          hazardous:      false,
          mode:           'In',
          sampleLoginOid: r.sampleLoginOid,
          jobOid:         r.jobOid,
        },
        ...prev,
      ]);
      setBarcode('');
    } catch {
      notify.error('Scan failed. Please try again.');
    } finally {
      scanBusy.current = false;
      setScanning(false);
      // Refocus handled by the effect watching `scanning` above — focusing here
      // would hit the still-disabled input (disabled={scanning}) and be a no-op.
    }
  }, [rows, userName, notify]);

  // ── Unlink — remove the selected (unsaved) rows from the working grid. ───────
  const unlink = useCallback(async () => {
    if (selected.size === 0) { notify.warning('Select a scanned row to unlink.'); return; }
    const ok = await confirm.confirm({
      title: 'Unlink',
      message: `Remove ${selected.size} scanned bottle(s) from this batch?`,
      tone: 'danger',
    });
    if (!ok) return;
    setRows(prev => prev.filter(r => !selected.has(r.id)));
    setSelected(new Set());
    notify.success("Sample 'Bottle ID' unlinked successfully.");
  }, [selected, confirm, notify]);

  // ── Save — the shared flow ("Do you want to Save?" → persist → "Saved
  //    successfully.") then clear the grid for the next batch. Create is
  //    reason-exempt (auditAction 'create'). ─────────────────────────────────
  const save = useCallback(() => {
    // ALPACA SaveAction_Executing parity (SampleInOutController, §18.11b): with no
    // scanned bottle, distinguish an EMPTY scan box from a typed-but-unresolved
    // barcode so the user gets the right prompt instead of no feedback (the Save
    // button no longer hard-disables on an empty grid — user report 2026-09-25:
    // "validation is missing when without entering the bottle id").
    if (rows.length === 0) {
      notify.error(barcode.trim()
        ? "Please enter a valid 'Bottle ID'."
        : "Please scan the sample 'Bottle ID'.");
      return;
    }
    setSaving(true);
    runSave({
      formName: 'Sample In',
      auditAction: 'create',
      save: async () => {
        await api.post('/api/v1/sample-custody/sample-in', {
          to,
          scans: rows.map(r => ({
            sampleBottleId: r.sampleBottleId,
            sampleLoginOid: r.sampleLoginOid,
            jobOid:         r.jobOid,
            client:         r.client,
            hazardous:      r.hazardous,   // user-ticked on the grid (ALPACA parity)
          })),
        });
        setRows([]);
        setSelected(new Set());
      },
      successMessage: `Saved ${rows.length} bottle(s) to Sample In.`,
    }).finally(() => setSaving(false));
  }, [rows, to, barcode, runSave, notify]);

  // ── Cancel — discard the current (unsaved) batch. ───────────────────────────
  const cancel = useCallback(async () => {
    if (rows.length > 0) {
      const ok = await confirm.confirm({
        title: 'Cancel', message: `Discard ${rows.length} scanned bottle(s)?`, tone: 'danger',
      });
      if (!ok) return;
    }
    setRows([]); setSelected(new Set()); setBarcode('');
  }, [rows, confirm]);

  // ── "To" options — one flat, alphabetical destination list (ALPACA parity:
  //    storages + users from GetToValues, merged and sorted). ──────────────────
  const toOptions = useMemo(
    () => [...opts.storages, ...opts.users]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [opts]);

  // In-place row patch — lets the editable Hazardous checkbox toggle a row
  // (ALPACA parity: Hazardous is user-editable on Sample In, default off).
  const updateRow = useCallback((id: string, patch: Partial<ScanRow>) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  // ── Grid columns — the prototype's 7-column "Barcode Transaction" grid ───────
  const cols: AppColumn<ScanRow>[] = useMemo(() => [
    { field: 'sampleBottleId', header: 'SampleBottleID', width: 170 },
    { field: 'from',           header: 'From',           width: 130 },
    { field: 'to',             header: 'To',             width: 150 },
    { field: 'scannedAt',      header: 'DateHandled',    width: 160,
      // Date-only (ALPACA parity: the SampleIn list views carry no DisplayFormat,
      // so DevExpress renders DateHandled as short date — no time). §14b.6.
      text: r => fmtLocalDate(r.scannedAt) },
    { field: 'handledBy',      header: 'HandledBy',      width: 130 },
    { field: 'hazardous',      header: 'Hazardous',      width: 100,
      // Editable on Sample In (ALPACA parity) — the user ticks it to flag the
      // bottle hazardous; it is saved to dbo.SampleIn.Hazardous and then carried
      // forward read-only on Sample Out / Disposal.
      render: r => (
        <input type="checkbox" checked={r.hazardous} disabled={!canWrite}
          onClick={e => e.stopPropagation()}
          onChange={e => updateRow(r.id, { hazardous: e.target.checked })} />
      ),
      text: r => (r.hazardous ? 'Yes' : 'No') },
    { field: 'mode',           header: 'Mode', width: 90,
      render: r => <span className="bl-pill ok">{r.mode}</span>, text: r => r.mode },
  ], [updateRow, canWrite]);

  // Phase 2.3 — Sample In keys (CLAUDE.md standing rule 14, §18.6, ADR-0013):
  // Ctrl+S Save · Alt+U Unlink · Esc Cancel — mirror the top/bottom action bar
  // (same handlers + disabled gates).  Esc is allowInInput:false so it never
  // discards the batch while typing in the barcode field.
  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: 'Sample In', order: 1,
      run: () => save(), disabled: saving || !canWrite },
    { combo: 'alt+u', label: 'Unlink', group: 'Sample In', order: 3,
      run: () => { void unlink(); }, disabled: selected.size === 0 || !canWrite },
    { combo: 'alt+x', label: 'Export to', group: 'Sample In', order: 7,
      run: () => exportRef.current?.open() },
    { combo: 'esc', label: 'Cancel', group: 'Sample In', order: 9, allowInInput: false,
      run: () => { void cancel(); }, disabled: saving },
  ]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="bl-page">

      {/* Fixed top: breadcrumb + title */}
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Sample Tracking</span>
            <i className="ti ti-chevron-right" />
            <span>Barcode Sample Custody</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Sample In</span>
          </div>
          <div className="dp-title-row">
            {/* ONE combined action row (user request 2026-10-10): the load-time chip
                on the LEFT (standing rule 7 — at the top next to the title) and
                Save / Cancel on the RIGHT — no separate white action card. It lives
                in the fixed .bl-top header, so Save stays visible while the grid
                scrolls. The grid toolbar passes showMeta={false}. */}
            <span className="si-load-chip"><LoadTimeChip ms={loadMs} loading={loading} /></span>
            <div className="si-top-actions">
              <button className="ca-btn ca-btn-solid" onClick={save}
                disabled={saving || !canWrite}
                title={(!canWrite ? 'You are not authorized to save on Sample In.' : 'Save') + ` (${formatCombo('mod+s')})`}>
                {saving ? <><LoaderSymbol size={13} /> Saving…</> : <><span className="tb-i">💾</span> Save</>}
              </button>
              <button className="ca-btn" onClick={() => void cancel()} disabled={saving} title={`Cancel (${formatCombo('esc')})`}>
                <span className="tb-i" style={{ color: 'var(--ai-blue, #185fa5)' }}>↩</span> Cancel
              </button>
            </div>
          </div>
          <div className="dp-subtitle">
            Pick a destination, then scan bottle barcodes to check them in · {rows.length} scanned
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="bl-grid-area">

        {/* Barcode Scan + To band */}
        <div className="bl-card" style={{ padding: 12, marginBottom: 8 }}>
          {/* Read-only access (ALPACA parity): a user without Write on "Sample In"
              (Module Access Control) may VIEW the form but cannot enter data —
              the scan input, destination and Save are all disabled. */}
          {!canWrite && (
            <div style={{ marginBottom: 10, fontSize: 12.5, color: '#8a5a00', background: '#fff7e6',
              border: '1px solid #ffd591', borderRadius: 4, padding: '7px 10px' }}>
              <span className="tb-i">🔒</span> You have read-only access to Sample In — data entry is disabled.
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '0 0 auto' }}>
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Barcode Scan</span>
              <input
                ref={barcodeRef}
                type="text"
                style={{ width: 380, maxWidth: '100%', flex: 'none', ...REQ_FIELD }}
                placeholder="Scan or type a bottle barcode…"
                value={barcode}
                disabled={scanning || !canWrite}
                onChange={e => setBarcode(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void scanCode(barcode, to); } }}
                onBlur={() => { if (barcode.trim() && to) void scanCode(barcode, to); }}
              />
              {scanning && <LoaderSymbol size={14} />}
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '0 0 auto' }}>
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>To</span>
              <div style={{ width: 360, maxWidth: '100%' }}>
                {/* Shared ComboSelect — compact scrollable popup + type-to-search,
                    flat destination list (ALPACA parity). */}
                <ComboSelect
                  className="si-to-combo"
                  value={to}
                  options={toOptions}
                  disabled={!canWrite}
                  onChange={(v) => { setTo(v); if (barcode.trim() && v) void scanCode(barcode, v); }}
                  placeholder="— Select destination —"
                />
              </div>
            </label>
          </div>
        </div>

        {/* Unlink + a labelled "Export to" live ON the grid's own header strip
            (headerLeft) — above the column headers, inside the grid card — matching
            legacy ALPACA's "Barcode Transaction" grid toolbar (owner decision
            2026-10-10: the labelled button over the built-in icon). hideHeaderExport
            suppresses the grid's built-in Export icon so there is ONE Export control;
            the Maximize icon stays at the grid's top-right (§18.6). */}
        <div className="bl-card bl-card-flex">
          <AppDataGrid<ScanRow>
            className="custody-grid"
            flushRight
            rows={rows}
            columns={cols}
            getRowId={r => r.id}
            initialSort={{ key: 'sampleBottleId', dir: 'asc' }}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            hideHeaderExport
            emptyText="No data to display — scan a barcode above."
            headerLeft={
              <>
                <button className="ca-btn" onClick={() => void unlink()} disabled={selected.size === 0 || !canWrite} title={`Unlink selected (${formatCombo('alt+u')})`}>
                  <span className="tb-i">🔗</span> Unlink{selected.size > 0 ? ` (${selected.size})` : ''}
                </button>
                <ExportMenu ref={exportRef} filename="SampleIn" {...gridExportMatrix(rows, cols)}
                  triggerClassName="ca-btn" title={`Export to (${formatCombo('alt+x')})`} />
              </>
            }
          />
        </div>
      </div>
    </div>
  );
}
